import React from 'react';
import { useWindowDimensions } from 'react-native';
import isEqual from 'lodash/isEqual';
import useData, { UseDataResult, Pagination, DataOptions } from '../../hooks/useData';
import useLinkedData from '../../hooks/useLinkedData';
import calculateDynamicPageSize from '../calculateDynamicPageSize';
import {
  lifecycleConfig, AdminModelMap, AdminObjectName, LifecycleConfigEntry,
} from '../DataColumnConfig';
import type { UseDataTableProps } from '../DataTable/types';

export interface UseDataTableResult<K extends AdminObjectName> {
  currentPage: number;
  currentPageSize: number;
  setCurrentPage: (page: number) => void;
  setCurrentPageSize: (size: number) => void;
  searchText: string | null;
  setSearchText: (text: string | null) => void;
  sortField: string | null;
  sortDirection: 'asc' | 'desc';
  setSortField: (field: string | null) => void;
  setSortDirection: (direction: 'asc' | 'desc') => void;
  rowSelection: Record<number, boolean>;
  setRowSelection: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
  isSelectionMode: boolean;
  setIsSelectionMode: (val: boolean) => void;
  selectedStates: string[];
  setSelectedStates: React.Dispatch<React.SetStateAction<string[]>>;
  includeDeprecated: boolean;
  setIncludeDeprecated: (val: boolean) => void;
  displayData: AdminModelMap[K][];
  tablePagination: Pagination | null;
  isLoading: boolean;
  handleSearch: (text: string | null) => void;
  handlePageChange: (page: number) => void;
  handleSort: (field: string) => void;
  selectedRows: AdminModelMap[K][];
  objectLifecycleConfig: {
    states?: { id: string; label: string }[];
    defaultStates?: string[];
    supportsDeprecation?: boolean;
  };
  TABLE_HEIGHTS: {
    header: number;
    tableHeader: number;
    functionBar: number;
    paddingVertical: number;
    row: number;
    pagination: number;
    extraFooter: number;
  };
  searchableFields?: string[];
  defaultSearchField?: string;
  onClearSelection: () => void;
  onExitSelectionMode: () => void;
}

/**
 * useDataTable - A comprehensive hook for managing the state and logic of a DataTable.
 *
 * It encapsulates:
 * - Integration with `useData` or `useLinkedData` for automated API fetching.
 * - Local state for pagination (page, size), sorting (field, direction), and searching.
 * - Selection state management (single/bulk).
 * - Debounced filter logic to prevent excessive API calls.
 * - Lifecycle configuration mapping for specific object types (e.g., default states).
 * - Layout calculations (TABLE_HEIGHTS).
 *
 * @param {UseDataTableProps} props - Configuration objects for data, display, filters, and callbacks.
 * @returns {object} An object containing all states and handlers required by the DataTable view.
 */
export const useDataTable = <K extends AdminObjectName>({
  dataConfig,
  displayConfig,
  searchFilterConfig = {},
  paginationSelectionConfig = {},
  callbacks = {},
}: UseDataTableProps<K>): UseDataTableResult<K> => {
  const {
    objectName,
    parentObjectName,
    parentId,
    data,
    pagination,
    disableDataFetching = false,
    isLoading: externalIsLoading,
    refreshKey,
    rowFilter,
  } = dataConfig;

  const { columns, heightsOverrides } = displayConfig;

  const { height: screenHeight } = useWindowDimensions();

  const {
    filter = {},
    defaultSearchField: defaultSearchFieldProp,
    searchableFields: searchableFieldsProp,
  } = searchFilterConfig;

  // Stabilize the filter object to prevent infinite re-renders if a new object is passed as a prop
  const filterString = JSON.stringify(filter);
  const stableFilterProp = React.useMemo(() => JSON.parse(filterString), [filterString]);

  const { pageSize } = paginationSelectionConfig;

  const {
    onSearch,
    onPageChange,
    onSelectionChange,
    onClearSelection,
    onExitSelectionMode,
  } = callbacks;

  const TABLE_HEIGHTS = React.useMemo(
    () => ({
      header: heightsOverrides?.header ?? 140,
      tableHeader: heightsOverrides?.tableHeader ?? 44,
      functionBar: heightsOverrides?.functionBar ?? 45, // Assumption: showSearch is usually true
      paddingVertical: heightsOverrides?.paddingVertical ?? 10,
      row: heightsOverrides?.row ?? 44,
      pagination: heightsOverrides?.pagination ?? 45,
      extraFooter: heightsOverrides?.extraFooter ?? 0,
    }),
    [heightsOverrides],
  );

  const initialPageSize = React.useMemo(() => {
    if (pageSize) return pageSize;
    return calculateDynamicPageSize({ heights: TABLE_HEIGHTS, screenHeight });
  }, [pageSize, TABLE_HEIGHTS, screenHeight]);

  const initialSort = React.useMemo(() => {
    const defaultSortColumn = Object.entries(columns || {}).find(([, col]) => col.defaultSort);
    if (defaultSortColumn) {
      return {
        field: defaultSortColumn[0],
        direction: (defaultSortColumn[1].defaultSort === 'desc' ? 'desc' : 'asc') as 'asc' | 'desc',
      };
    }
    return { field: null, direction: 'asc' as 'asc' | 'desc' };
  }, [columns]);

  const [currentPage, setCurrentPage] = React.useState(1);
  const [currentPageSize, setCurrentPageSize] = React.useState(pageSize || initialPageSize);
  const [searchText, setSearchText] = React.useState<string | null>(null);
  const [sortField, setSortField] = React.useState<string | null>(initialSort.field);
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>(initialSort.direction);
  const [rowSelection, setRowSelection] = React.useState<Record<number, boolean>>({});
  const [isSelectionMode, setIsSelectionMode] = React.useState(false);
  const [previousData, setPreviousData] = React.useState<AdminModelMap[K][]>([]);
  const [previousPagination, setPreviousPagination] = React.useState<Pagination | null>(pagination || null);

  // When disableDataFetching is true, sync internal page state with provided pagination
  React.useEffect(() => {
    if (disableDataFetching && pagination?.page) {
      setCurrentPage(pagination.page);
    }
  }, [disableDataFetching, pagination?.page]);

  // When disableDataFetching is true, sync internal pageSize with provided pagination
  React.useEffect(() => {
    if (disableDataFetching && pagination?.pageSize) {
      setCurrentPageSize(pagination.pageSize);
    }
  }, [disableDataFetching, pagination?.pageSize]);

  const objectLifecycleConfig = React.useMemo(
    () => (lifecycleConfig as Record<string, LifecycleConfigEntry>)[objectName] || { supportsDeprecation: false },
    [objectName],
  );
  const [selectedStates, setSelectedStates] = React.useState<string[]>(objectLifecycleConfig.defaultStates || []);
  const [includeDeprecated, setIncludeDeprecated] = React.useState(false);

  const activeFilter = React.useMemo(() => {
    const baseFilter = { ...stableFilterProp };
    if (objectLifecycleConfig.states && selectedStates.length > 0 && !stableFilterProp.state) {
      baseFilter.state = selectedStates;
    }
    if (objectLifecycleConfig.supportsDeprecation && !includeDeprecated && stableFilterProp.isDeprecated === undefined) {
      baseFilter.isDeprecated = false;
    }
    return baseFilter;
  }, [stableFilterProp, objectLifecycleConfig, selectedStates, includeDeprecated]);

  const [debouncedFilter, setDebouncedFilter] = React.useState(activeFilter);
  const [isPendingFilter, setIsPendingFilter] = React.useState(false);

  const isFirstRun = React.useRef(true);
  const previousActiveFilterRef = React.useRef(activeFilter);

  React.useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      previousActiveFilterRef.current = activeFilter;
      return () => {};
    }

    if (JSON.stringify(activeFilter) === JSON.stringify(previousActiveFilterRef.current)) {
      return () => {};
    }

    previousActiveFilterRef.current = activeFilter;
    setIsPendingFilter(true);
    const timer = setTimeout(() => {
      setDebouncedFilter(activeFilter);
      setIsPendingFilter(false);
    }, 800);
    return () => {
      clearTimeout(timer);
    };
  }, [activeFilter]);

  React.useEffect(() => {
    setRowSelection({});
    setIsSelectionMode(false);
  }, [objectName, refreshKey]);

  React.useEffect(() => {
    if (!pageSize && currentPageSize !== initialPageSize) {
      setCurrentPageSize(initialPageSize);
      setCurrentPage(1);
    }
  }, [initialPageSize, pageSize, currentPageSize]);

  React.useEffect(() => {
    if (objectLifecycleConfig.defaultStates) {
      setSelectedStates(objectLifecycleConfig.defaultStates);
    }
    setIncludeDeprecated(false);
  }, [objectName, objectLifecycleConfig.defaultStates]);

  const isLinkedData = !!(parentObjectName && parentId);
  const dataHook = isLinkedData
    ? (useLinkedData as unknown as (props: DataOptions<K>) => UseDataResult<AdminModelMap[K]>)
    : (useData as unknown as (props: DataOptions<K>) => UseDataResult<AdminModelMap[K]>);
  const hookResult: UseDataResult<AdminModelMap[K]> = !disableDataFetching
    ? dataHook({
      objectName,
      parentObjectName: parentObjectName || undefined,
      parentId: parentId || undefined,
      filter: debouncedFilter,
      searchText,
      currentPage,
      sort: sortField ? (sortDirection === 'desc' ? `${sortField}-` : sortField) : undefined,
      currentPageSize,
      defaultSearchField: defaultSearchFieldProp,
      searchableFields: searchableFieldsProp,
    })
    : {
      data: (data || []) as AdminModelMap[K][],
      isLoading: false,
      isPreviousData: false,
      error: null,
      pagination: pagination || { page: 1, pageSize: 10, total: 0 },
      createMutation: null as unknown as UseDataResult<AdminModelMap[K]>['createMutation'],
      updateMutation: null as unknown as UseDataResult<AdminModelMap[K]>['updateMutation'],
    };

  const tableData = disableDataFetching ? data : hookResult.data;
  const hasValidPagination = hookResult.pagination
    && typeof hookResult.pagination.total === 'number'
    && !hookResult.isPreviousData;
  const tablePagination = disableDataFetching
    ? pagination
    : hasValidPagination
      ? hookResult.pagination
      : previousPagination;
  const isLoading = (disableDataFetching ? (externalIsLoading || false) : hookResult.isLoading) || isPendingFilter;

  // Use a ref to track the last values we set to state to prevent infinite loops
  // in environments where the hookResult returns new references on every render.
  const lastSetDataRef = React.useRef<AdminModelMap[K][] | null>(null);
  const lastSetPaginationRef = React.useRef<Pagination | null>(null);

  React.useEffect(() => {
    if (!isLoading && tableData && tableData.length > 0) {
      if (!isEqual(tableData, lastSetDataRef.current)) {
        lastSetDataRef.current = tableData;
        setPreviousData(tableData);
      }
    }
    if (!isLoading && hasValidPagination && hookResult.pagination) {
      if (!isEqual(hookResult.pagination, lastSetPaginationRef.current)) {
        lastSetPaginationRef.current = hookResult.pagination;
        setPreviousPagination(hookResult.pagination);
      }
    }
  }, [isLoading, tableData, hasValidPagination, hookResult.pagination]);

  const displayData = React.useMemo(() => {
    const baseData = isLoading ? previousData || [] : tableData || previousData || [];
    const filteredData = rowFilter ? baseData.filter(rowFilter) : baseData;

    if (disableDataFetching) {
      const isServerPaginatedData = !!pagination
        && typeof pagination.total === 'number'
        && pagination.total > filteredData.length;
      if (isServerPaginatedData) {
        return filteredData;
      }
      const startIndex = (currentPage - 1) * currentPageSize;
      const endIndex = startIndex + currentPageSize;
      return filteredData.slice(startIndex, endIndex);
    }

    return filteredData;
  }, [isLoading, tableData, previousData, rowFilter, disableDataFetching, pagination, currentPage, currentPageSize]);

  const handleSearch = (text: string | null) => {
    if (disableDataFetching) {
      onSearch?.(text || '');
    } else {
      setSearchText(text);
      setCurrentPage(1);
    }
  };

  const handlePageChange = (page: number) => {
    if (disableDataFetching) {
      setCurrentPage(page);
      onPageChange?.(page);
    } else {
      setCurrentPage(page);
    }
  };

  const handleSort = (field: string) => {
    if (!columns[field]?.sortable) return;

    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortField(null);
        setSortDirection('asc');
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const selectedRows = React.useMemo(() => {
    if (!isSelectionMode) return [];
    return Object.keys(rowSelection)
      .map((idx) => displayData[parseInt(idx, 10)])
      .filter(Boolean);
  }, [rowSelection, displayData, isSelectionMode]);

  React.useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedRows);
    }
  }, [selectedRows, onSelectionChange]);

  return {
    currentPage,
    currentPageSize,
    setCurrentPage,
    setCurrentPageSize,
    searchText,
    setSearchText,
    sortField,
    sortDirection,
    setSortField,
    setSortDirection,
    rowSelection,
    setRowSelection,
    isSelectionMode,
    setIsSelectionMode,
    selectedStates,
    setSelectedStates,
    includeDeprecated,
    setIncludeDeprecated,
    displayData,
    tablePagination,
    isLoading,
    handleSearch,
    handlePageChange,
    handleSort,
    selectedRows,
    objectLifecycleConfig,
    TABLE_HEIGHTS,
    searchableFields: searchableFieldsProp,
    defaultSearchField: defaultSearchFieldProp,
    onClearSelection: () => {
      setRowSelection({});
      onClearSelection?.();
    },
    onExitSelectionMode: () => {
      setIsSelectionMode(false);
      onExitSelectionMode?.();
    },
  };
};
